// Story 1.20 — TanStack mutation for POST /api/v1/account/rectification-request.
//
// The body's `requested_changes` is a JSON object the user fills in (the
// `reason` field is required; the other keys are arbitrary at v1). The
// SPA's RectificationForm renders this as a free-form Textarea + a
// dedicated reason field; the hook just submits.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type RequestRectificationBody = {
  requested_changes: Record<string, unknown>;
};

export type RequestRectificationResponse = {
  rectification_request_id: string;
  status: "submitted";
  estimated_response_in_days: number;
};

export type RequestRectificationError = {
  code: string;
  message: string;
  field?: string;
};

async function postRectificationRequest(
  body: RequestRectificationBody,
  idempotencyKey: string,
): Promise<RequestRectificationResponse> {
  const response = await fetch("/api/v1/account/rectification-request", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": idempotencyKey,
    },
    body: JSON.stringify(body),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: RequestRectificationError = {
      code: "RECTIFICATION_REQUEST_FAILED",
      message: `Rectification request failed (HTTP ${response.status})`,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = { ...raw.error };
      }
    } catch {
      // Body not JSON.
    }
    throw detail;
  }
  const envelope = (await response.json()) as {
    data: RequestRectificationResponse;
  };
  return envelope.data;
}

export function useRequestRectification() {
  const queryClient = useQueryClient();
  return useMutation<
    RequestRectificationResponse,
    RequestRectificationError,
    { body: RequestRectificationBody }
  >({
    mutationFn: ({ body }) =>
      postRectificationRequest(body, crypto.randomUUID()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dsar-status"] });
    },
  });
}
